import { BreakdownDisplayMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/breakdown-display-mode";
import { Currency } from "@redotech/money/currencies";
import { CorrelatedCarrierServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  DynamicRateRounding,
  RateTableType,
} from "./shipping-rate-table-enums";

/**
 * Shopify's carrier service API sends us the shop's base currency, not the
 * customer's browsing currency. We guess the customer's currency from their
 * destination country and pre-convert the landed cost breakdown into that
 * currency. When the guess is correct, the checkout UI can use these values
 * directly — avoiding a second conversion that would introduce rounding
 * mismatches between the breakdown and the total price Shopify displays.
 */
const landedCostAmountsSchema_ = z.object({
  duties: z.number(),
  taxes: z.number(),
  fees: z.number(),
});

const customerCurrencyEstimateSchema_ = z.object({
  duties: z.number(), // minor units in guessed customer currency
  taxes: z.number(), // minor units in guessed customer currency
  fees: z.number(), // minor units in guessed customer currency
  currency: createEnum(Currency, { enumName: "Currency" }),
  exchangeRate: z.number(), // shop currency → guessed customer currency
  original: landedCostAmountsSchema_.optional(),
});
export const customerCurrencyEstimateSchema: z.ZodType<CustomerCurrencyEstimate> =
  customerCurrencyEstimateSchema_;
export interface CustomerCurrencyEstimate extends z.infer<
  typeof customerCurrencyEstimateSchema_
> {}

const landedCostDataSchema_ = z.object({
  zonosQuoteId: z.string(),
  duties: z.number(), // minor units, shop currency
  taxes: z.number(), // minor units, shop currency
  fees: z.number(), // minor units, shop currency
  currency: createEnum(Currency, { enumName: "Currency" }),
  /** Present only when the customer's guessed currency differs from the shop's. */
  customerCurrencyEstimate: customerCurrencyEstimateSchema.optional(),
  original: landedCostAmountsSchema_.optional(),
  displayMode: createEnum(BreakdownDisplayMode, {
    enumName: "BreakdownDisplayMode",
  }).optional(),
  breakdownLabelOverrides: z
    .object({
      shipping: z.string().optional(),
      taxes: z.string().optional(),
      dutiesAndFees: z.string().optional(),
    })
    .optional(),
});
export const landedCostDataSchema: z.ZodType<LandedCostData> =
  landedCostDataSchema_;
export interface LandedCostData extends z.infer<typeof landedCostDataSchema_> {}

const baseRedoShippingOptionSchema = z.object({
  provider: z.literal("redo"),
  rateTableId: zExt.objectId(),
  serviceCodeSentToCheckoutWhenMetafieldsUsed: z.string().optional(),
  checkoutTreeNodeId: z.string(),
  checkoutExperienceId: z.string().optional(),
  assignedTreatmentId: z.string().optional(),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
  landedCostData: landedCostDataSchema.optional(),
});

const markupSchema = z.object({
  markupPercentage: z.string().optional(),
  markupAmount: z.string().optional(),
});

const costBreakdownSchema = z.object({
  costCurrency: createEnum(Currency, { enumName: "Currency" }),
  labelCost: z.string(),
  markup: markupSchema.optional(),
  rounding: createEnum(DynamicRateRounding, {
    enumName: "DynamicRateRounding",
  }).optional(),
});
export interface CostBreakdown extends z.infer<typeof costBreakdownSchema> {}

const dynamicRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.DYNAMIC),
  carrierService: CorrelatedCarrierServiceSchema,
  /**
   * Multiple parcels can cause a rate table to have multiple carrier services
   */
  carrierServices: z.array(CorrelatedCarrierServiceSchema).optional(),
  costBreakdown: costBreakdownSchema,
  promisedDeliveryDate: z.string().optional(),
});

const fixedRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.FIXED),
});

const customRedoShippingOptionSchema = baseRedoShippingOptionSchema.extend({
  rateType: z.literal(RateTableType.CUSTOM),
});

export const checkoutShippingOptionSchema_ = z.union([
  dynamicRedoShippingOptionSchema,
  fixedRedoShippingOptionSchema,
  customRedoShippingOptionSchema,
]);
export const checkoutShippingOptionSchema: z.ZodType<CheckoutShippingOption> =
  checkoutShippingOptionSchema_;
export type CheckoutShippingOption = z.infer<
  typeof checkoutShippingOptionSchema_
>;
